import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { toast } from "react-toastify";
import { IconsArray } from "../../../../../constants/Icons";
import {
  agencyTargetLocations,
  targetLocations,
} from "../../../../../constants/TargetLocations";

const DEFAULT_FORM = {
  button_text: "",
  color: "#4f46e5",
  tooltip: "",
  button_class: "",
  action_type: "Target",
  targetLocation: "",
  link: "",
  icon: "",
  iconFamily: "fas",
  onlyIcon: false,
};

const AddHeaderButton = ({
  handleRefresh,
  data,
  editIndex = null,
  selectedButton = null,
  onClose = () => {},
}) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const locationsPaths =
    type === "agency" ? agencyTargetLocations : targetLocations;

  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const isEdit = editIndex !== null && selectedButton;

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [iconSearch, setIconSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && selectedButton) {
      setFormData({
        ...DEFAULT_FORM,
        ...selectedButton,
      });
    }
  }, [isEdit, selectedButton]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (!isEdit) {
      setFormData(DEFAULT_FORM);
      setIconSearch("");
    }
    onClose();
  };

  const handleChange = (e) => {
    const { name, type: t, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: t === "checkbox" ? checked : value,
    }));
  };

  const filteredIcons = useMemo(() => {
    const term = iconSearch.trim().toLowerCase();
    if (!term) return IconsArray;
    return IconsArray.filter((i) => i.name.toLowerCase().includes(term));
  }, [iconSearch]);

  const setIcon = (name, family) => {
    setFormData((prev) => ({ ...prev, icon: name, iconFamily: family }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.onlyIcon && !formData.button_text.trim()) {
      toast.warning("Button text is required (or enable 'Icon only' mode).");
      return;
    }
    if (formData.onlyIcon && !formData.icon) {
      toast.warning("Pick an icon when 'Icon only' is enabled.");
      return;
    }
    if (formData.action_type === "Target" && !formData.targetLocation) {
      toast.warning("Please select a target location.");
      return;
    }
    if (formData.action_type === "Link" && !formData.link.trim()) {
      toast.warning("Please provide a valid link.");
      return;
    }

    const button = {
      button_text: formData.onlyIcon ? "" : formData.button_text,
      color: formData.color,
      tooltip: formData.tooltip,
      button_class: formData.button_class,
      action_type: formData.action_type,
      targetLocation:
        formData.action_type === "Target" ? formData.targetLocation : null,
      link: formData.action_type === "Link" ? formData.link : null,
      icon: formData.icon || null,
      iconFamily: formData.icon ? formData.iconFamily : null,
      onlyIcon: !!formData.onlyIcon,
    };

    const header_buttons = data?.header_buttons ? [...data.header_buttons] : [];
    if (isEdit) header_buttons[editIndex] = button;
    else header_buttons.push(button);

    const payload = {
      header_buttons,
      companyId,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      setSubmitting(true);
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        toast.success(isEdit ? "Button updated!" : "Button added!");
        handleRefresh();
        setOpen(false);
        if (!isEdit) {
          setFormData(DEFAULT_FORM);
          setIconSearch("");
        }
        onClose();
      } else {
        toast.error("Failed to save button.");
      }
    } catch (error) {
      console.error("Save header button error:", error);
      toast.error("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewBorderRadius =
    formData.onlyIcon || !formData.button_text ? "50%" : "8px";
  const previewSize = formData.onlyIcon ? "36px" : "auto";

  return (
    <>
      {isEdit ? (
        <button
          className="btn d-inline-flex align-items-center justify-content-center gap-1"
          title="Edit"
          onClick={handleOpen}
          style={{
            borderRadius: 8,
            background: "#eef2ff",
            color: "#4338ca",
            border: "1px solid #c7d2fe",
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 10px",
          }}
        >
          <i className="bi bi-pencil-square" />
          Edit
        </button>
      ) : (
        <button
          className="btn btn-primary d-inline-flex align-items-center gap-2"
          onClick={handleOpen}
          style={{ borderRadius: 10, fontWeight: 500 }}
        >
          <i className="bi bi-plus-lg" />
          Add Header Button
        </button>
      )}

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", zIndex: 2000 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <style>{`
            .hb-modal .modal-dialog {
              max-height: calc(100vh - 3rem);
              margin-top: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .hb-modal .modal-content {
              border-radius: 16px;
              overflow: hidden;
              border: none;
              box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
              max-height: calc(100vh - 3rem);
              display: flex;
              flex-direction: column;
            }
            .hb-modal form {
              display: flex;
              flex-direction: column;
              flex: 1 1 auto;
              min-height: 0;
              overflow: hidden;
            }
            .hb-modal .modal-body {
              flex: 1 1 auto;
              overflow-y: auto;
              min-height: 0;
            }
            .hb-modal .modal-body::-webkit-scrollbar { width: 8px; }
            .hb-modal .modal-body::-webkit-scrollbar-track { background: transparent; }
            .hb-modal .modal-body::-webkit-scrollbar-thumb {
              background: rgba(15, 23, 42, 0.18);
              border-radius: 8px;
            }
            .hb-modal .modal-body::-webkit-scrollbar-thumb:hover {
              background: rgba(15, 23, 42, 0.32);
            }
            .hb-modal .hb-header { flex-shrink: 0; }
            .hb-modal .hb-header {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              color: #fff;
              padding: 18px 22px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .hb-modal .hb-header h5 {
              margin: 0;
              font-weight: 600;
              font-size: 18px;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .hb-modal .hb-header small {
              color: rgba(255, 255, 255, 0.8);
              font-size: 12px;
            }
            .hb-modal .hb-close {
              background: rgba(255, 255, 255, 0.15);
              border: none;
              color: #fff;
              width: 32px;
              height: 32px;
              border-radius: 8px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              transition: background 0.15s;
            }
            .hb-modal .hb-close:hover { background: rgba(255, 255, 255, 0.28); }
            .hb-modal .hb-section-title {
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #475569;
              margin-bottom: 10px;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .hb-modal .hb-card {
              background: #fff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 16px;
            }
            .hb-modal .hb-preview {
              background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
              border: 1px dashed #c7d2fe;
              border-radius: 12px;
              padding: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 110px;
            }
            .hb-modal .form-label {
              font-size: 13px;
              font-weight: 500;
              color: #334155;
              margin-bottom: 6px;
            }
            .hb-modal .form-control,
            .hb-modal .form-select {
              border-radius: 8px;
              border-color: #e2e8f0;
              font-size: 14px;
            }
            .hb-modal .form-control:focus,
            .hb-modal .form-select:focus {
              border-color: #818cf8;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .hb-modal .color-picker-wrap {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .hb-modal .color-picker-wrap input[type="color"] {
              width: 44px;
              height: 38px;
              padding: 2px;
              border-radius: 8px;
              border: 1px solid #e2e8f0;
              cursor: pointer;
              flex-shrink: 0;
            }
            .hb-modal .type-toggle {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            .hb-modal .type-toggle button {
              border: 1px solid #e2e8f0;
              background: #fff;
              border-radius: 10px;
              padding: 10px 12px;
              font-size: 13px;
              font-weight: 500;
              color: #475569;
              transition: all 0.15s;
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: center;
            }
            .hb-modal .type-toggle button.active {
              background: #eef2ff;
              border-color: #818cf8;
              color: #4338ca;
            }
            .hb-modal .icon-search {
              position: relative;
              margin-bottom: 10px;
            }
            .hb-modal .icon-search .bi-search {
              position: absolute;
              left: 12px;
              top: 50%;
              transform: translateY(-50%);
              color: #94a3b8;
            }
            .hb-modal .icon-search input { padding-left: 36px; }
            .hb-modal .icon-grid {
              display: grid;
              grid-template-columns: repeat(8, 1fr);
              gap: 6px;
              max-height: 200px;
              overflow-y: auto;
              padding: 6px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
            }
            .hb-modal .icon-cell {
              aspect-ratio: 1;
              border: 1px solid transparent;
              background: #fff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.12s;
              color: #475569;
              font-size: 16px;
            }
            .hb-modal .icon-cell:hover {
              border-color: #c7d2fe;
              transform: translateY(-1px);
              color: #4338ca;
            }
            .hb-modal .icon-cell.active {
              background: #eef2ff;
              border-color: #4f46e5;
              color: #4338ca;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
            }
            .hb-modal .icon-info {
              font-size: 12px;
              color: #64748b;
              margin-top: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .hb-modal .icon-clear {
              border: none;
              background: transparent;
              color: #ef4444;
              font-size: 12px;
              font-weight: 500;
              padding: 0;
            }
            .hb-modal .icon-clear:hover { text-decoration: underline; }
            .hb-modal .toggle-row {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              padding: 10px 12px;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
              background: #fff;
              cursor: pointer;
              user-select: none;
              transition: all 0.15s;
            }
            .hb-modal .toggle-row:hover { border-color: #c7d2fe; background: #f8fafc; }
            .hb-modal .toggle-row input { margin-top: 3px; cursor: pointer; flex-shrink: 0; }
            .hb-modal .toggle-row .toggle-label {
              font-size: 13px;
              font-weight: 500;
              color: #1e293b;
              display: block;
            }
            .hb-modal .toggle-row .toggle-help {
              font-size: 12px;
              color: #64748b;
              margin-top: 1px;
            }
            .hb-modal .modal-footer {
              border-top: 1px solid #e2e8f0;
              padding: 14px 22px;
              background: #f8fafc;
              flex-shrink: 0;
            }
            .hb-modal .btn-cancel {
              background: transparent;
              color: #64748b;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-weight: 500;
            }
            .hb-modal .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
            .hb-modal .btn-save {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              border: none;
              color: #fff;
              border-radius: 8px;
              font-weight: 500;
              padding: 8px 22px;
            }
            .hb-modal .btn-save:hover { opacity: 0.92; color: #fff; }
            .hb-modal .btn-save:disabled { opacity: 0.6; }
          `}</style>

          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg hb-modal">
            <div className="modal-content">
              <div className="hb-header">
                <div>
                  <h5>
                    <i
                      className={`bi ${
                        isEdit ? "bi-pencil-square" : "bi-plus-square"
                      }`}
                    />
                    {isEdit ? "Edit Header Button" : "Add Header Button"}
                  </h5>
                  <small>
                    Configure a button that appears in the GHL top header.
                  </small>
                </div>
                <button
                  type="button"
                  className="hb-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  {/* Live preview */}
                  <div className="hb-preview mb-4">
                    <button
                      type="button"
                      tabIndex={-1}
                      title={formData.tooltip}
                      style={{
                        background: formData.color,
                        color: "#fff",
                        border: "none",
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        borderRadius: previewBorderRadius,
                        width: previewSize,
                        height: previewSize,
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
                      }}
                    >
                      {formData.icon && (
                        <i
                          className={`${formData.iconFamily} fa-${formData.icon}`}
                          style={{ fontSize: 14 }}
                        />
                      )}
                      {!formData.onlyIcon && (
                        <span>{formData.button_text || "Button preview"}</span>
                      )}
                    </button>
                  </div>

                  {/* Identity */}
                  <div className="hb-card mb-3">
                    <div className="hb-section-title">
                      <i className="bi bi-tag" /> Identity
                    </div>
                    <div className="row g-3">
                      <div className="col-md-7">
                        <label className="form-label">Button text</label>
                        <input
                          type="text"
                          name="button_text"
                          className="form-control"
                          value={formData.button_text}
                          onChange={handleChange}
                          placeholder="e.g. Help"
                          disabled={formData.onlyIcon}
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Background color</label>
                        <div className="color-picker-wrap">
                          <input
                            type="color"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                          />
                          <input
                            type="text"
                            className="form-control"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            placeholder="#4f46e5"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Tooltip</label>
                        <input
                          type="text"
                          name="tooltip"
                          className="form-control"
                          value={formData.tooltip}
                          onChange={handleChange}
                          placeholder="Shown on hover"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="hb-card mb-3">
                    <div className="hb-section-title">
                      <i className="bi bi-emoji-smile" /> Icon (optional)
                    </div>
                    <div className="icon-search">
                      <i className="bi bi-search" />
                      <input
                        type="text"
                        className="form-control"
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        placeholder="Search icons..."
                      />
                    </div>

                    <div className="icon-grid">
                      {filteredIcons.length === 0 ? (
                        <div
                          className="text-center text-muted small py-3"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          No icons match "{iconSearch}"
                        </div>
                      ) : (
                        filteredIcons.map((ic) => (
                          <div
                            key={`${ic.fontFamily}-${ic.name}`}
                            className={`icon-cell ${
                              formData.icon === ic.name &&
                              formData.iconFamily === ic.fontFamily
                                ? "active"
                                : ""
                            }`}
                            onClick={() => setIcon(ic.name, ic.fontFamily)}
                            title={ic.name}
                          >
                            <i className={`${ic.fontFamily} fa-${ic.name}`} />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="icon-info">
                      <span>
                        {formData.icon ? (
                          <>
                            Selected: <strong>{formData.icon}</strong>
                          </>
                        ) : (
                          <span className="text-muted">No icon selected</span>
                        )}
                      </span>
                      {formData.icon && (
                        <button
                          type="button"
                          className="icon-clear"
                          onClick={() => setIcon("", "fas")}
                        >
                          <i className="bi bi-x-circle me-1" />
                          Remove icon
                        </button>
                      )}
                    </div>

                    <label className="toggle-row mt-3">
                      <input
                        type="checkbox"
                        name="onlyIcon"
                        checked={formData.onlyIcon}
                        onChange={handleChange}
                        disabled={!formData.icon}
                      />
                      <span>
                        <span className="toggle-label">Icon only</span>
                        <span className="toggle-help">
                          Render the button as a circular icon-only badge (hides
                          the text).
                        </span>
                      </span>
                    </label>
                  </div>

                  {/* Behaviour */}
                  <div className="hb-card mb-3">
                    <div className="hb-section-title">
                      <i className="bi bi-arrow-up-right-circle" /> Click
                      behaviour
                    </div>

                    <div className="type-toggle mb-3">
                      <button
                        type="button"
                        className={
                          formData.action_type === "Target" ? "active" : ""
                        }
                        onClick={() =>
                          setFormData((p) => ({ ...p, action_type: "Target" }))
                        }
                      >
                        <i className="bi bi-bullseye" />
                        Internal target
                      </button>
                      <button
                        type="button"
                        className={
                          formData.action_type === "Link" ? "active" : ""
                        }
                        onClick={() =>
                          setFormData((p) => ({ ...p, action_type: "Link" }))
                        }
                      >
                        <i className="bi bi-link-45deg" />
                        External link
                      </button>
                    </div>

                    {formData.action_type === "Target" && (
                      <div>
                        <label className="form-label">Target location</label>
                        <select
                          name="targetLocation"
                          className="form-select"
                          value={formData.targetLocation}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a location...</option>
                          {locationsPaths.map((loc, i) => (
                            <option key={i} value={loc}>
                              {loc}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.action_type === "Link" && (
                      <div>
                        <label className="form-label">External URL</label>
                        <input
                          type="url"
                          name="link"
                          className="form-control"
                          value={formData.link}
                          onChange={handleChange}
                          placeholder="https://..."
                        />
                      </div>
                    )}
                  </div>

                  {/* Advanced */}
                  <div className="hb-card">
                    <div className="hb-section-title">
                      <i className="bi bi-sliders" /> Advanced
                    </div>
                    <label className="form-label">CSS class / id</label>
                    <input
                      type="text"
                      name="button_class"
                      className="form-control"
                      value={formData.button_class}
                      onChange={handleChange}
                      placeholder="my-header-action"
                    />
                    <p className="text-muted small mb-0 mt-2">
                      Used as the rendered button's id and class for custom
                      styling.
                    </p>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : isEdit ? (
                      "Update button"
                    ) : (
                      "Create button"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddHeaderButton;
